import React, { useCallback } from 'react';
import momentTz from 'moment-timezone';
import { BigCalendar } from 'lib/ui';
import * as PropTypes from 'prop-types';
import { useReactiveVar } from '@apollo/client';
import { ProviderColumnHeader } from './CalendarHeaders';
import Actions from 'pages/provider/profile/header/Actions';

import Toolbar from 'views/components/calendar/mainCalendar/toolbar';
import CalendarEvent from 'views/components/calendar/mainCalendar/event';
import { CalendarWrapper } from 'views/components/calendar/mainCalendar/MainCalendar.style';
import { customEventPropGetter, customizeRange, localizer } from 'utils/builders/calendar';
import {
  staffCalendarPrefDayVar,
  staffCalendarPrefViewVar,
  views
} from 'utils/cache/calendar/index';
import {
  STAFF_CALENDAR_MIN_HEIGHT,
  STAFF_CALENDAR_MAX_HEIGHT
} from 'utils/constants/calendar';

import { IProvider } from 'model/v2';
import { ICalendarEvent } from 'model/calendar/events';
import { View } from 'react-big-calendar';
import { CALENDAR_MODES, CalendarModeName } from './calendarModes';
import { useProviderTemplate } from './useProviderTemplate';

const formats = {
  dayFormat: 'ddd DD'
};

interface CustomEventProps {
  event: ICalendarEvent;
}

interface ScheduleCalendarProps {
  provider: IProvider;
  /**
   * Which calendar this is. `template` is the DT recurring template from
   * AXON-3578; `legacy` is every other department's provider calendar. The
   * differences between them live in CALENDAR_MODES, not in this file.
   */
  mode?: CalendarModeName;
}

const ScheduleCalendar = ({
  provider,
  mode: modeName = 'legacy'
}: ScheduleCalendarProps) => {
  const mode = CALENDAR_MODES[modeName];
  const calendarDate = useReactiveVar(staffCalendarPrefDayVar);
  const calendarView = useReactiveVar(staffCalendarPrefViewVar) as View;

  const handleRangeChange = useCallback(date => {
    staffCalendarPrefDayVar(date);
  }, []);

  const handleViewChange = useCallback(inputView => {
    staffCalendarPrefViewVar(inputView);
  }, []);

  const {
    events,
    totalEvents,
    sessionData,
    providerWithSessions
  } = useProviderTemplate({ provider, mode, calendarDate, calendarView });

  // Every time on this grid — template blocks and appointments alike — is the
  // provider's own clock, so the grid has to say whose clock that is.
  const timezone = provider?.clinic?.timezone;
  // Read the abbreviation on the week being shown, not on today. Paging from
  // July back to January would otherwise label PST bars as PDT — the legend
  // disagreeing with the grid, which is the thing this set out to remove.
  const timezoneAbbr = timezone
    ? momentTz(calendarDate)
        .tz(timezone)
        .format('z')
    : '';

  const customToolbar = React.memo(data => {
    return (
      <Toolbar
        data={data}
        updateView={handleViewChange}
        range={customizeRange(calendarDate, calendarView)}
        leftContent={
          mode.showsTemplateToolbar ? (
            <div className="template-title">
              <h3
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#21303a'
                }}
              >
                Provider Recurring Template
              </h3>
              {timezoneAbbr && (
                // role="note" so the aria-label is actually announced —
                // assistive tech ignores a name on a element with no role.
                <span
                  className="tz-chip"
                  role="note"
                  aria-label={`All times shown in ${timezoneAbbr}, the provider's time zone`}
                >
                  All times in {timezoneAbbr}
                </span>
              )}
            </div>
          ) : (
            undefined
          )
        }
        rightContent={
          mode.showsTemplateToolbar ? <Actions provider={provider} /> : undefined
        }
      />
    );
  });

  const customEvent = React.memo(({ event }: CustomEventProps) => {
    return (
      <>
        <CalendarEvent
          event={event}
          view={calendarView}
          provider
          redesign={mode.isTemplate}
        />
      </>
    );
  });

  const customToolTipOfAvailability = useCallback(event => {
    if (event.type === 'availability') return event.title;
  }, []);

  const columnHeader = useCallback(
    (headerProps: { date: Date; label?: string }) => (
      <ProviderColumnHeader
        {...headerProps}
        provider={providerWithSessions}
        showSessions={calendarView === views.WEEK}
        templateSessions={sessionData?.templateSessions}
        isTemplate={mode.isTemplate}
      />
    ),
    [providerWithSessions, calendarView, sessionData, mode.isTemplate]
  );

  const timeGutterHeader = useCallback(
    () => (
      <div
        className="provider-time-gutter"
        role={timezoneAbbr ? 'note' : undefined}
        aria-label={timezoneAbbr ? `Time column, ${timezoneAbbr}` : undefined}
      >
        {timezoneAbbr}
      </div>
    ),
    [timezoneAbbr]
  );

  const calendarComponents = mode.showsSessionHeaders
    ? {
        toolbar: customToolbar,
        event: customEvent,
        header: columnHeader,
        timeGutterHeader: timeGutterHeader
      }
    : {
        toolbar: customToolbar,
        event: customEvent
      };

  const eventPropGetter = mode.compactsShortAppointments
    ? (evt: any) => {
        const res = customEventPropGetter(evt) as {
          className?: string;
          style?: object;
        };
        const durationMin =
          (new Date(evt.end).getTime() - new Date(evt.start).getTime()) / 60000;
        if (
          durationMin <= 30 &&
          evt.type !== 'na' &&
          evt.type !== 'availability'
        ) {
          res.className = `${res.className || ''} appt-compact`;
        }
        return res;
      }
    : (evt: any) => customEventPropGetter({ ...evt, bgColor: undefined });

  // The month view drops non-working-hours blocks, which would otherwise fill
  // every cell. Only the legacy mode reaches month at all.
  const visibleEvents =
    !mode.foldsIntoStandingWeek && calendarView === views.MONTH
      ? totalEvents.filter(e => e.type !== 'na')
      : events;

  return (
    <CalendarWrapper className={calendarView}>
      <BigCalendar
        view={calendarView}
        onView={handleViewChange}
        events={visibleEvents}
        views={mode.views}
        drilldownView={mode.drilldownView}
        defaultView={views.WEEK}
        min={mode.minTime}
        max={mode.maxTime}
        scrollToTime={mode.scrollToTime}
        components={calendarComponents}
        date={calendarDate}
        onNavigate={handleRangeChange}
        step={mode.step}
        timeslots={mode.timeslots}
        localizer={localizer}
        slotEventOverlap={false} // TODO: check usage of this prop
        formats={formats}
        eventPropGetter={eventPropGetter}
        style={{
          height:
            calendarView !== views.MONTH
              ? STAFF_CALENDAR_MAX_HEIGHT
              : STAFF_CALENDAR_MIN_HEIGHT
        }}
        tooltipAccessor={customToolTipOfAvailability}
        className="schedule-calendar"
      />
    </CalendarWrapper>
  );
};

ScheduleCalendar.propTypes = {
  uiState: PropTypes.object
};

export default React.memo(ScheduleCalendar);
